import React from "react";
import cn from "classnames";
import { Box, Grid, Container as TeddyContainer, Video } from "@telia/teddy";
import { DynamicBlock, trackingContext } from "../../../../../../models";
import { FAQ as FaqBlock } from "../../molecules/FAQ/FAQ";
import ContentBlock from "../../molecules/ContentBlock/ContentBlock";
import NavigationCardBlock from "../../molecules/NavigationCardBlock/NavigationCardBlock";
import UspList from "../../molecules/UspList/UspList";
import LoginCard from "../../molecules/LoginCard/LoginCard";
import CampaignTextBlock from "../../molecules/CampaignTextBlock/CampaignTextBlock";
import SubscriptionTerms from "../../molecules/SubscriptionTerms/SubscriptionTerms";
import SubscriptionTextBlock from "../../molecules/SubscriptionTextBlock/SubscriptionTextBlock";
import "./DynamicBlocks.less";
import { trackUIClickEvent } from "../../../../google-analytics/googleAnalyticsEventHelper";

interface DynamicBlocksProps {
    blocks: DynamicBlock[];
    fullWidth?: boolean;
    idPrefix?: string;
    isWhiteBg?: boolean;
    onSignIn?: () => Promise<void>;
    stepIndicatorList?: React.ReactElement;
    personalFamilyDiscountCard?: React.ReactElement;
    orderDetailsCard?: React.ReactElement;
    shouldRenderLoginCard?: boolean;
    trackingContext?: trackingContext;
    shouldRenderPersonalFamilyDiscountCard?: boolean;
}

const DynamicBlocks: React.FC<DynamicBlocksProps> = ({
    idPrefix = "",
    isWhiteBg = true,
    fullWidth = false,
    trackingContext,
    blocks,
    shouldRenderLoginCard,
    shouldRenderPersonalFamilyDiscountCard,
    personalFamilyDiscountCard,
    orderDetailsCard,
    stepIndicatorList,
    onSignIn,
}) => (
    <Box pl="200" pr="200" pt="0" className="dynamic-blocks">
        <TeddyContainer
            maxWidth={{ sm: "1244px" }}
            className={cn("dynamic-blocks__container", {
                "dynamic-blocks__white": isWhiteBg,
            })}
            mr="auto"
            ml="auto"
            mt="0"
            mb="0"
            p="0"
        >
            {blocks.map((block, index) => {
                const id = `db-${idPrefix}-${index + 1}`;
                switch (block.__type) {
                    case "StepIndicatorList":
                        return (
                            <Box maxWidth={fullWidth ? undefined : { lg: "660px" }} width="100%" id={id} key={id}>
                                {stepIndicatorList}
                            </Box>
                        );
                    case "OrderDetailsCard":
                        return (
                            <Box width="100%" id={id} key={id}>
                                {orderDetailsCard}
                            </Box>
                        );
                    case "FAQList":
                        return (
                            <Box maxWidth={{ lg: "660px" }} width="100%" mb="200" id={id} key={id}>
                                <FaqBlock trackingContext={trackingContext} items={block.faq} title={block.title} />
                            </Box>
                        );
                    case "TextBlock":
                        return (
                            <Box maxWidth={fullWidth ? undefined : { lg: "660px" }} width="100%" id={id} key={id}>
                                {block.position === "campaign" ? (
                                    <CampaignTextBlock key={block.heading} textBlock={block} />
                                ) : (
                                    <SubscriptionTextBlock
                                        textBlock={block}
                                        shouldCenterOnDesktop={block.centerContentOnDesktop}
                                    />
                                )}
                            </Box>
                        );
                    case "Terms":
                        return (
                            <Box maxWidth={fullWidth ? undefined : { lg: "660px" }} width="100%" id={id} key={id}>
                                <SubscriptionTerms content={block.content} />
                            </Box>
                        );
                    case "LoginCard":
                        if (shouldRenderLoginCard) {
                            return (
                                <Box
                                    id={id}
                                    key={id}
                                    maxWidth={{ sm: "32.75rem" }}
                                    width="100%"
                                    m="auto"
                                    mb={{ sm: "200", lg: "400" }}
                                >
                                    <LoginCard
                                        variant={block.backgroundColor}
                                        icon={block.icon}
                                        title={block.title}
                                        content={block.content}
                                        onClick={async () => {
                                            if (trackingContext) {
                                                await trackUIClickEvent({
                                                    action: "click",
                                                    context: `${trackingContext.prefix}_login_card`,
                                                    text: block.title,
                                                    type: "button",
                                                    url: trackingContext.url,
                                                });
                                            }
                                            onSignIn && onSignIn();
                                        }}
                                    />
                                </Box>
                            );
                        }
                        if (shouldRenderPersonalFamilyDiscountCard) {
                            return (
                                <Box
                                    key={id}
                                    maxWidth={{ sm: "660px" }}
                                    width="100%"
                                    m="auto"
                                    mb={{ sm: "200", lg: "400" }}
                                >
                                    {personalFamilyDiscountCard}
                                </Box>
                            );
                        }
                        return null;
                    case "NavigationCardList":
                        return (
                            <Box maxWidth={fullWidth ? undefined : { lg: "660px" }} width="100%" id={id} key={id}>
                                <Grid justify="stretch" className="dynamic-blocks__navigation-cards">
                                    {block.navigationCards.map((navigationCard) => (
                                        <NavigationCardBlock
                                            url={navigationCard.url}
                                            image={navigationCard.image?.url}
                                            heading={navigationCard.heading}
                                            key={navigationCard.url}
                                        />
                                    ))}
                                </Grid>
                            </Box>
                        );
                    case "USPList":
                        return (
                            <Box maxWidth={{ lg: "660px" }} width="100%" id={id} key={id}>
                                <UspList {...block} trackingContext={trackingContext} />
                            </Box>
                        );
                    case "ContentBlock":
                        return (
                            <TeddyContainer
                                maxWidth={fullWidth ? undefined : { lg: block.size === "contain" ? "660px" : "1440px" }}
                                width="100%"
                                mt="200"
                                mr="0"
                                ml="0"
                                pt="0"
                                key={id}
                            >
                                <Box
                                    id={id}
                                    className={cn("dynamic-blocks__banner", {
                                        "dynamic-blocks__banner-container-border": block.showBorder,
                                    })}
                                >
                                    <ContentBlock
                                        badge={block.badge}
                                        image={block.image}
                                        title={block.title}
                                        titleTag={block.titleTag}
                                        text={block.text}
                                        buttons={block.buttons}
                                        variant={block.variant}
                                    />
                                </Box>
                            </TeddyContainer>
                        );
                    case "ContentBlockGrid":
                        return (
                            <TeddyContainer
                                maxWidth={fullWidth ? undefined : { lg: "660px" }}
                                mr="0"
                                ml="0"
                                pt="0"
                                id={id}
                                key={id}
                            >
                                <Grid columns={{ sm: "1", md: "2" }} gap={{ sm: "200" }}>
                                    {block.contentBlocks.map((contentBlock, idx) => (
                                        <ContentBlock
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`${id}-content-block-${idx}`}
                                            badge={contentBlock.badge}
                                            image={contentBlock.image}
                                            title={contentBlock.title}
                                            titleTag={contentBlock.titleTag}
                                            text={contentBlock.text}
                                            buttons={contentBlock.buttons}
                                            variant={contentBlock.variant}
                                            layout="rich-card"
                                        />
                                    ))}
                                </Grid>
                            </TeddyContainer>
                        );
                    case "YouTubeVideo":
                        return (
                            <Box
                                maxWidth={fullWidth ? undefined : { lg: "1440px" }}
                                width="100%"
                                id={id}
                                key={id}
                                mt="200"
                            >
                                <div className="dynamic-blocks__full-width">
                                    <Video
                                        src={`https://www.youtube.com/embed/${block.videoId}`}
                                        description={block.description}
                                        allowFullScreen
                                    />
                                </div>
                            </Box>
                        );
                    case "YouTubeVideoGrid":
                        return (
                            <Box maxWidth={fullWidth ? undefined : { lg: "1440px" }} width="100%" id={id} key={id}>
                                <div className="dynamic-blocks__full-width-grid">
                                    {block.videos.map((video) => (
                                        <Video
                                            key={video.videoId}
                                            src={`https://www.youtube.com/embed/${video.videoId}`}
                                            description={video.description}
                                            allowFullScreen
                                        />
                                    ))}
                                </div>
                            </Box>
                        );
                    default:
                        return null;
                }
            })}
        </TeddyContainer>
    </Box>
);

export default DynamicBlocks;
